import { useState, useEffect } from "react";
import { IDongGoi, IProductLog } from "../../models";
import { searchEsProduct, getProductLogs } from "../../api";
import { Form } from "react-bootstrap";
import { Button } from "../shared";

interface ProductLogProps {
    onGoBack: () => void;
    pharmacyId: string;
    productId: string;
    adminId: string;
}

export default function ProductLog({
    onGoBack,
    pharmacyId,
    productId,
    adminId
}: ProductLogProps) {
    const [logs, setLogs] = useState<IProductLog[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [packageTypes, setPackageTypes] = useState<IDongGoi[]>([]);
    const [filter, setFilter] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);

    const itemsPerPage: number = 20;

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const findDifferences = (records: IProductLog[]) => {
        for (let i = 0; i < records.length - 1; i++) {
            const currentRecord = records[i];

            for (let j = i + 1; j < records.length; j++) {
                if (records[j].mDongGoi?.id === currentRecord.mDongGoi?.id) {
                    const nextRecord = records[j];
                    for (const key in currentRecord) {
                        if (currentRecord[key] !== nextRecord[key] && 
                            ["mId", "mCreated", "mLastUpdateBy", "mLastUpdateByName", "mLastUpdateById", "mDongGoi", "mPharmacyProductMap"].indexOf(key) === -1) {
                                if (currentRecord.change === undefined) currentRecord.change = [key];
                                else currentRecord.change.push(key);
                            }
                    }
                    break;
                }
            }
        }
        return records;
    }

    useEffect(() => {
        Promise.all([
            searchEsProduct({
                _source: ['dong_goi'],
                query: {
                    match: {
                        _id: productId
                    },
                }
            }),
            getProductLogs(pharmacyId, productId, adminId)
        ]).then(([esProducts, productMapLogs]) => {
            if (!esProducts || !productMapLogs) return;

            setPackageTypes(esProducts.data[0].dong_goi);

            const logsWithoutDifference = productMapLogs.map((item) => ({
                ...item,
                mDongGoi: esProducts.data[0].dong_goi.find((dg: IDongGoi) => dg.id === item.mPharmacyProductMap.mPackageId)
            }));
            const logsWithDifference = findDifferences(logsWithoutDifference);
            setLogs(logsWithDifference);
            setLoading(false);
        })
    }, []);

    const displayDatetime = (datetime: string) => {
        const date = new Date(datetime);
        return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`
    }

    const displayPackage = (dongGoi: IDongGoi | undefined) => {
        if (!dongGoi) return "";
        if (dongGoi.loai_dong_goi.name == dongGoi.don_vi.name) {
            return `${dongGoi.so_luong} ${dongGoi.don_vi.name}`
        }
        return `${dongGoi.loai_dong_goi.name} ${dongGoi.so_luong} ${dongGoi.don_vi.name}`
    }

    const filteredLogs = filter ? logs.filter((item) => item.mDongGoi?.id === filter) : logs;

    const totalPages: number = Math.ceil(filteredLogs.length / itemsPerPage);

    const startIndex: number = (currentPage - 1) * itemsPerPage;
    const endIndex: number = startIndex + itemsPerPage;

    const currentPageData: IProductLog[] = filteredLogs.slice(startIndex, endIndex);

    return (
        <div className="pt-3 pb-3">
            <h3>Lịch sử chỉnh sửa</h3>
            <p><i>Ghi chú: những giá trị in đậm là giá trị thay đổi so với cùng loại đóng gói trước đó</i></p>

            <div className="w-50 d-flex align-items-center">
                <Form.Label className='m-0 mr-1'><b>Đóng gói</b></Form.Label>
                <Form.Control onChange={(e) => {
                    setFilter(e.target.value);
                    setCurrentPage(1);
                }
                } name='filter' as="select" value={filter} custom>
                    <option value=''>Tất cả</option>
                    {packageTypes.map((item, index) => (
                        <option key={index} value={item.id}>{displayPackage(item)}</option>
                    ))}
                </Form.Control>
            </div>

            <div className="log-table">
                <table>
                    <thead>
                        <tr>
                            <th className="log-th">Ngày chỉnh sửa</th>
                            <th className="log-th">Người chỉnh sửa</th>
                            <th className="log-th">Đóng gói</th>
                            <th className="log-th">Giá</th>
                            <th className="log-th">Giá gốc</th>
                            <th className="log-th">Trạng thái</th>
                            <th className="log-th">Còn hàng</th>
                            <th className="log-th">SL đã bán</th>
                        </tr>
                    </thead>

                    <tbody>
                        {currentPageData.map((item, index) => (
                            <tr key={index}>
                                <td>{displayDatetime(item.mCreated)}</td>
                                <td>{item.mLastUpdateBy? `${item.mLastUpdateByName} (${item.mLastUpdateBy})`:''}</td>
                                <td>{displayPackage(item.mDongGoi)}</td>
                                <td className={(item.change && item.change.includes("mPrice"))? "change":""}>{item.mPrice}</td>
                                <td className={(item.change && item.change.includes("mOriginalPrice"))? "change":""}>{item.mOriginalPrice}</td>
                                <td className={(item.change && item.change.includes("mStatus"))? "change":""}>{item.mStatus}</td>
                                <td className={(item.change && item.change.includes("mInStock"))? "change":""}>{item.mInStock ? "Còn hàng" : "Hết hàng"}</td>
                                <td className={(item.change && item.change.includes("mSoldAmount"))? "change":""}>{item.mSoldAmount}</td>
                            </tr>
                        ))}
                    </tbody>
                    
                    <tfoot>
                        <tr>
                            <td colSpan={8}>
                                {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                                <button
                                    key={pageNumber}
                                    onClick={() => handlePageChange(pageNumber)}
                                    disabled={currentPage === pageNumber}
                                    className="log-page-btn"
                                >
                                    {pageNumber}
                                </button>
                                ))}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <Button 
                onClick={onGoBack}
                loading={loading}
                disabled={loading}
                text="Quay lại"
            />
        </div>
    );
}
